/*
 * Claude as a helper in the Steward (apps/api claude/). An owner or manager connects Claude to one location from Settings,
 * Claude; it then works there as that person, with their role, through the same routes and checks as the app, and never
 * opens any other company (not the brand, not HQ, not another location: tenancy/company.ts claudeLock). A few things stay
 * with people even when their role allows them: sending to clients, moving money, keys, security, and who gets in.
 */
import { z } from "zod";
import type { Action } from "./permissions.js";
import type { Role } from "./core.js";

/** Never done through Claude, whatever the role. */
export const CLAUDE_BLOCKED: readonly Action[] = [
  "card.charge",
  "dispute.send",
  "keys.read",
  "keys.update",
  "security.read",
  "security.update",
  "people.delete",
  "people.update_roles",
  "payouts.settings",
  "company.create",
  "outreach.send",
  // Nothing above or across the location: reports over the companies below, head office, the franchise's own plan and card.
  "report.children",
  "hq.read",
  "hq.update",
  "franchise.update",
  // Who gets in stays with people.
  "people.invite",
];

/** Owners and managers only (owner, 2026-11-02: no dispatch, no crew). */
export const CLAUDE_ROLES: readonly Role[] = ["owner", "franchise_owner", "manager"];

export function claudeMay(action: Action): boolean {
  return !CLAUDE_BLOCKED.includes(action);
}

/** The Connect Claude page asks who is asking, from the link Claude opened. */
export const claudeClientQuery = z.object({
  clientId: z.string().min(1).max(4000),
  redirectUri: z.string().min(1).max(2000),
});
export const claudeClientAnswer = z.object({ name: z.string(), redirectHost: z.string() });

/** Allow: the page sends back what Claude asked for, and the company picked. */
export const claudeApproveBody = claudeClientQuery.extend({
  companyId: z.uuid(),
  codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/, "The link from Claude is missing its check. Start again in Claude."),
  codeChallengeMethod: z.literal("S256"),
  state: z.string().max(2000).optional(),
});
export const claudeApproveAnswer = z.object({ redirect: z.string() });
